"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PatientErrorCatalog = exports.PatientErrorCodes = void 0;
exports.getPatientErrorCatalogEntry = getPatientErrorCatalogEntry;
/**
 * Patient Identity-specific error codes — namespaced `PATIENT_*`,
 * additive and parallel to backend's own `ErrorCodes`/`ErrorCatalog`
 * (`backend/src/shared/constants/error-codes.constant.ts`,
 * `backend/src/core/exception/error-catalog.ts`), which this package
 * does not import or modify (contracts has no dependency on backend
 * source, by design — see the package README). Each code here maps
 * to a real, named business rule already enforced inside a frozen
 * Domain Service (Stage 4C.2) — this file does not invent new rules,
 * only gives the ones that already exist a stable, documented code a
 * client can branch on.
 */
exports.PatientErrorCodes = {
    PATIENT_CONTACT_METHOD_REQUIRED: 'PATIENT_CONTACT_METHOD_REQUIRED',
    PATIENT_NOT_FOUND: 'PATIENT_NOT_FOUND',
    PATIENT_ALREADY_MERGED: 'PATIENT_ALREADY_MERGED',
    MERGE_SELF_MERGE_NOT_ALLOWED: 'MERGE_SELF_MERGE_NOT_ALLOWED',
    MERGE_RESTRICTED_BY_PROTECTION: 'MERGE_RESTRICTED_BY_PROTECTION',
    MERGE_SESSION_NOT_FOUND: 'MERGE_SESSION_NOT_FOUND',
    MERGE_ROLLBACK_INVALID_STATE: 'MERGE_ROLLBACK_INVALID_STATE',
    COVERAGE_PRIMARY_ALREADY_ACTIVE: 'COVERAGE_PRIMARY_ALREADY_ACTIVE',
    COVERAGE_NOT_FOUND: 'COVERAGE_NOT_FOUND',
    COVERAGE_CLOSED_RECORD: 'COVERAGE_CLOSED_RECORD',
    CONSENT_NOT_FOUND: 'CONSENT_NOT_FOUND',
    CONSENT_NOT_ACTIVE: 'CONSENT_NOT_ACTIVE',
    CONSENT_GUARDIAN_INVALID: 'CONSENT_GUARDIAN_INVALID',
    PROTECTION_ALREADY_ACTIVE: 'PROTECTION_ALREADY_ACTIVE',
    PROTECTION_NOT_FOUND: 'PROTECTION_NOT_FOUND',
    PROTECTION_INVALID_STATE: 'PROTECTION_INVALID_STATE',
    IDENTITY_CANDIDATE_NOT_FOUND: 'IDENTITY_CANDIDATE_NOT_FOUND',
    IDENTITY_CANDIDATE_ALREADY_REVIEWED: 'IDENTITY_CANDIDATE_ALREADY_REVIEWED',
    IDENTITY_SELF_COMPARISON_NOT_ALLOWED: 'IDENTITY_SELF_COMPARISON_NOT_ALLOWED',
    VALIDATION_ERROR: 'PATIENT_VALIDATION_ERROR',
};
exports.PatientErrorCatalog = {
    [exports.PatientErrorCodes.PATIENT_CONTACT_METHOD_REQUIRED]: {
        httpStatus: 400,
        description: 'A patient cannot be registered without at least one contact method.',
        retryable: false,
    },
    [exports.PatientErrorCodes.PATIENT_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No patient matches the given id (or is not visible to this tenant).',
        retryable: false,
    },
    [exports.PatientErrorCodes.PATIENT_ALREADY_MERGED]: {
        httpStatus: 409,
        description: 'A patient that is already merged cannot be merged again.',
        retryable: false,
    },
    [exports.PatientErrorCodes.MERGE_SELF_MERGE_NOT_ALLOWED]: {
        httpStatus: 409,
        description: 'Source and target patient must be different.',
        retryable: false,
    },
    [exports.PatientErrorCodes.MERGE_RESTRICTED_BY_PROTECTION]: {
        httpStatus: 409,
        description: 'One of the patients has an active merge-restricted RecordProtection.',
        retryable: false,
    },
    [exports.PatientErrorCodes.MERGE_SESSION_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No PatientMerge session matches the given id.',
        retryable: false,
    },
    [exports.PatientErrorCodes.MERGE_ROLLBACK_INVALID_STATE]: {
        httpStatus: 409,
        description: 'Only a completed, forward merge session can be rolled back.',
        retryable: false,
    },
    [exports.PatientErrorCodes.COVERAGE_PRIMARY_ALREADY_ACTIVE]: {
        httpStatus: 409,
        description: 'This patient already has an active PRIMARY coverage.',
        retryable: false,
    },
    [exports.PatientErrorCodes.COVERAGE_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No PatientCoverage matches the given id.',
        retryable: false,
    },
    [exports.PatientErrorCodes.COVERAGE_CLOSED_RECORD]: {
        httpStatus: 409,
        description: 'This coverage is already expired/cancelled — its terms cannot be edited.',
        retryable: false,
    },
    [exports.PatientErrorCodes.CONSENT_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No Consent matches the given id.',
        retryable: false,
    },
    [exports.PatientErrorCodes.CONSENT_NOT_ACTIVE]: {
        httpStatus: 409,
        description: 'Only an ACTIVE consent can be withdrawn or updated.',
        retryable: false,
    },
    [exports.PatientErrorCodes.CONSENT_GUARDIAN_INVALID]: {
        httpStatus: 409,
        description: "The named guardian's authority is not currently valid.",
        retryable: false,
    },
    [exports.PatientErrorCodes.PROTECTION_ALREADY_ACTIVE]: {
        httpStatus: 409,
        description: 'This protection type is already active on this entity.',
        retryable: false,
    },
    [exports.PatientErrorCodes.PROTECTION_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No RecordProtection matches the given id.',
        retryable: false,
    },
    [exports.PatientErrorCodes.PROTECTION_INVALID_STATE]: {
        httpStatus: 409,
        description: 'This protection is not in a state this operation can act on.',
        retryable: false,
    },
    [exports.PatientErrorCodes.IDENTITY_CANDIDATE_NOT_FOUND]: {
        httpStatus: 404,
        description: 'No IdentityResolutionCandidate matches the given id.',
        retryable: false,
    },
    [exports.PatientErrorCodes.IDENTITY_CANDIDATE_ALREADY_REVIEWED]: {
        httpStatus: 409,
        description: 'This candidate has already been reviewed and decided.',
        retryable: false,
    },
    [exports.PatientErrorCodes.IDENTITY_SELF_COMPARISON_NOT_ALLOWED]: {
        httpStatus: 400,
        description: 'Cannot resolve a patient against itself.',
        retryable: false,
    },
    [exports.PatientErrorCodes.VALIDATION_ERROR]: {
        httpStatus: 422,
        description: 'Request body failed schema validation.',
        retryable: false,
    },
};
function getPatientErrorCatalogEntry(code) {
    return exports.PatientErrorCatalog[code];
}
//# sourceMappingURL=patient-error-codes.js.map